import React, { useState, useCallback } from 'react';
import { Eye, UserX, UserCheck, Plus, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Table from '../components/Table';
import Modal from '../components/Modal';
import Form, { FormField, useForm } from '../components/Form';
import FilterBar from '../components/FilterBar';
import { StatusBadge } from '../components/StatusBadge';
import { useFetch, useFilters } from '../hooks';
import { patientService } from '../services/api';
import { formatDate } from '../utils/helpers';

/* ── Filter config ──────────────────────────────────────────────────────────── */
const FILTER_INITIAL = { search: '', status: '', dateFrom: '', dateTo: '' };

const FILTER_FIELDS = [
  {
    key: 'status', label: 'Status', type: 'select',
    options: [
      { value: 'active', label: 'Active' },
      { value: 'suspended', label: 'Suspended' },
    ],
  },
  { key: 'dateFrom', label: 'Joined From', type: 'date' },
  { key: 'dateTo',   label: 'Joined To',   type: 'date' },
];

const patientFilterFn = (p, f) => {
  if (f.search) {
    const q = f.search.toLowerCase();
    if (![p.name, p.email, p.mobile].join(' ').toLowerCase().includes(q)) return false;
  }
  if (f.status && p.status !== f.status) return false;
  if (f.dateFrom && p.created_at && p.created_at < f.dateFrom) return false;
  if (f.dateTo   && p.created_at && p.created_at > f.dateTo)   return false;
  return true;
};

/* ── Add Patient Form ───────────────────────────────────────────────────────── */
const AddPatientForm = ({ onSubmit, onClose }) => {
  const rules = {
    name:   { required: true },
    email:  { required: true, email: true },
    mobile: { required: true, mobile: true },
  };
  const { values, errors, handleChange, handleBlur, handleSubmit } = useForm(
    { name: '', email: '', mobile: '', gender: '', dob: '', blood_group: '' }, rules
  );

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label="Full Name" error={errors.name} required>
          <input name="name" value={values.name} onChange={handleChange} onBlur={handleBlur} className="input-field" placeholder="John Smith" />
        </FormField>
        <FormField label="Email" error={errors.email} required>
          <input name="email" type="email" value={values.email} onChange={handleChange} onBlur={handleBlur} className="input-field" placeholder="patient@email.com" />
        </FormField>
        <FormField label="Mobile" error={errors.mobile} required>
          <input name="mobile" value={values.mobile} onChange={handleChange} onBlur={handleBlur} className="input-field" placeholder="9876543210" />
        </FormField>
        <FormField label="Gender">
          <select name="gender" value={values.gender} onChange={handleChange} className="input-field">
            <option value="">Select</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </FormField>
        <FormField label="Date of Birth">
          <input name="dob" type="date" value={values.dob} onChange={handleChange} className="input-field" />
        </FormField>
        <FormField label="Blood Group">
          <select name="blood_group" value={values.blood_group} onChange={handleChange} className="input-field">
            <option value="">Select</option>
            {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(bg => <option key={bg} value={bg}>{bg}</option>)}
          </select>
        </FormField>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
        <button type="submit" className="btn-primary">Add Patient</button>
      </div>
    </Form>
  );
};

/* ── Page ───────────────────────────────────────────────────────────────────── */
const Patients = () => {
  const navigate = useNavigate();
  const { data: patients, loading, refetch } = useFetch(() => patientService.getAll());
  const [showAdd, setShowAdd] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const filterFn = useCallback(patientFilterFn, []);
  const { filters, onChange: onFilterChange, onReset, filtered, activeCount } = useFilters(FILTER_INITIAL, filterFn, patients);

  const handleToggle = async (patient) => {
    const newStatus = patient.status === 'active' ? 'inactive' : 'active';
    try {
      const res = await patientService.toggleStatus(patient.id, newStatus);
      toast.success(res?.message || `Patient ${newStatus}`);
      refetch();
    } catch { toast.error('Failed to update status'); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await patientService.delete(deleteTarget.id);
      toast.success(res?.message || 'Patient deleted');
      setDeleteTarget(null);
      refetch();
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed to delete'); }
    finally { setDeleting(false); }
  };

  const handleAdd = async (values) => {
    try {
      const res = await patientService.create(values);
      toast.success(res?.message || 'Patient added!');
      refetch();
      setShowAdd(false);
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed to add patient'); }
  };

  const columns = [
    {
      key: 'name', label: 'Patient', render: (v, row) => (
        <div className="flex items-center gap-3">
          {row.profile_img
            ? <img src={row.profile_img} alt={v} className="w-9 h-9 rounded-full object-cover shrink-0" />
            : <div className="w-9 h-9 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center text-purple-600 dark:text-purple-400 font-semibold text-sm shrink-0">{v?.[0]}</div>
          }
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{v}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{row.uid}</p>
          </div>
        </div>
      )
    },
    { key: 'email', label: 'Email', render: v => <span className="text-sm">{v}</span> },
    { key: 'mobile', label: 'Mobile', render: v => v || '—' },
    { key: 'gender', label: 'Gender', render: v => v ? v.charAt(0).toUpperCase() + v.slice(1) : '—' },
    { key: 'blood_group', label: 'Blood Group', render: v => v || '—' },
    { key: 'created_at', label: 'Joined', render: v => v ? formatDate(v) : '—' },
    { key: 'status', label: 'Status', render: v => <StatusBadge status={v} /> },
    {
      key: 'id', label: 'Actions', render: (_, row) => (
        <div className="flex items-center gap-1">
          <button onClick={() => navigate(`/patients/${row.id}`)}
            className="p-1.5 hover:bg-medihub-light dark:hover:bg-medihub-dark/20 rounded-lg transition-colors text-medihub-primary" title="View Details">
            <Eye className="w-4 h-4" />
          </button>
          <button onClick={() => handleToggle(row)}
            className={`p-1.5 rounded-lg transition-colors ${row.status === 'active' ? 'hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500' : 'hover:bg-green-50 dark:hover:bg-green-900/20 text-green-500'}`}
            title={row.status === 'active' ? 'Suspend' : 'Activate'}>
            {row.status === 'active' ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
          </button>
          <button onClick={() => setDeleteTarget(row)}
            className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors text-red-500" title="Delete">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )
    },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Patients</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Manage all registered patients</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="btn-primary flex items-center gap-1.5">
          <Plus className="w-4 h-4" />Add Patient
        </button>
      </div>

      {/* Filters */}
      <FilterBar
        filters={filters}
        onChange={onFilterChange}
        onReset={onReset}
        searchPlaceholder="Search by name, email, phone…"
        fields={FILTER_FIELDS}
        activeCount={activeCount}
      />

      {/* Table */}
      <Table
        title={`Patients (${filtered.length}${activeCount ? ` of ${patients?.length || 0}` : ''})`}
        columns={columns}
        data={filtered}
        loading={loading}
        exportFilename="patients"
      />

      {/* Delete Confirmation Modal */}
      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Patient" size="sm">
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Are you sure you want to delete <span className="font-semibold text-gray-900 dark:text-white">{deleteTarget?.name}</span>? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setDeleteTarget(null)} className="btn-secondary">Cancel</button>
            <button onClick={handleDelete} disabled={deleting}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Add Patient Modal */}
      <Modal isOpen={showAdd} onClose={() => setShowAdd(false)} title="Add New Patient" size="lg">
        <AddPatientForm onSubmit={handleAdd} onClose={() => setShowAdd(false)} />
      </Modal>
    </div>
  );
};

export default Patients;
